/**
 * ExpenseModal Component
 *
 * Модальное окно добавления расходов.
 *
 * Категории расходов:
 * - supplier - Поставщики
 * - acquiring - Эквайринг
 * - infrastructure - Инфраструктура
 * - marketing - Маркетинг (списывается из резервов)
 * - salary - Зарплаты
 * - refund - Возвраты
 * - other - Прочее
 */

import { AnimatePresence, motion } from "framer-motion";
import { Calendar, DollarSign, FileText, Save, Tag, X } from "lucide-react";
import type React from "react";
import { memo, useEffect, useState } from "react";

export interface ExpenseData {
  description: string;
  amount: number;
  currency: "USD" | "RUB";
  category:
    | "supplier"
    | "acquiring"
    | "infrastructure"
    | "marketing"
    | "salary"
    | "refund"
    | "other";
  date?: string; // ISO date
  supplier_id?: string;
}

interface ExpenseModalProps {
  isOpen: boolean;
  expense?: ExpenseData | null;
  onClose: () => void;
  onSave: (expense: ExpenseData) => Promise<void>;
}

const EXPENSE_CATEGORIES = [
  { value: "supplier", label: "Поставщики", icon: "📦" },
  { value: "acquiring", label: "Эквайринг", icon: "💳" },
  { value: "infrastructure", label: "Инфраструктура", icon: "⚙️" },
  { value: "marketing", label: "Маркетинг (из резервов)", icon: "📢" },
  { value: "salary", label: "Зарплаты", icon: "💰" },
  { value: "refund", label: "Возвраты", icon: "↩️" },
  { value: "other", label: "Прочее", icon: "📋" },
] as const;

const ExpenseModal: React.FC<ExpenseModalProps> = ({ isOpen, expense, onClose, onSave }) => {
  const [editingExpense, setEditingExpense] = useState<ExpenseData>({
    description: "",
    amount: 0,
    currency: "RUB",
    category: "other",
    date: new Date().toISOString().split("T")[0], // Today in YYYY-MM-DD format
  });
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof ExpenseData, string>>>({});

  // Reset form when modal opens/closes or expense changes
  useEffect(() => {
    if (isOpen) {
      if (expense) {
        setEditingExpense(expense);
      } else {
        setEditingExpense({
          description: "",
          amount: 0,
          currency: "RUB",
          category: "other",
          date: new Date().toISOString().split("T")[0],
        });
      }
      setErrors({});
    }
  }, [isOpen, expense]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof ExpenseData, string>> = {};

    if (!editingExpense.description.trim()) {
      newErrors.description = "Описание обязательно";
    }

    if (editingExpense.amount <= 0) {
      newErrors.amount = "Сумма должна быть больше 0";
    }

    if (!editingExpense.date) {
      newErrors.date = "Дата обязательна";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editingExpense);
      onClose();
    } catch (error) {
      console.error("Failed to save expense:", error);
      // Error handling can be improved with toast notifications
    } finally {
      setIsSaving(false);
    }
  };

  const handleCategoryChange = (category: ExpenseData["category"]) => {
    setEditingExpense({ ...editingExpense, category });
  };

  const formatMoney = (amount: number, currency: "USD" | "RUB"): string => {
    const symbol = currency === "USD" ? "$" : "₽";
    if (currency === "RUB") {
      return `${Math.round(amount)} ${symbol}`;
    }
    return `${amount.toFixed(2)} ${symbol}`;
  };

  if (!isOpen) {
    return null;
  }

  const selectedCategoryInfo = EXPENSE_CATEGORIES.find((c) => c.value === editingExpense.category);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
        <motion.div
          animate={{ opacity: 1 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          onClick={onClose}
        />
        <motion.div
          animate={{ scale: 1, opacity: 1 }}
          className="relative z-10 w-full max-w-lg border border-white/20 bg-[#080808] p-6 shadow-2xl"
          exit={{ scale: 0.9, opacity: 0 }}
          initial={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-bold font-display text-white text-xl">
              <DollarSign className="text-green-400" size={20} />
              ДОБАВИТЬ РАСХОД
            </h3>
            <button
              className="text-gray-500 transition-colors hover:text-white"
              disabled={isSaving}
              onClick={onClose}
              type="button"
            >
              <X size={20} />
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Description */}
            <div>
              <label
                className="mb-1.5 flex items-center gap-1 text-[10px] text-gray-500 uppercase"
                htmlFor="expense-description"
              >
                <FileText size={12} />
                Описание
              </label>
              <textarea
                className={`h-20 w-full border bg-black ${
                  errors.description ? "border-red-500" : "border-white/20"
                } resize-none p-3 text-sm text-white outline-none focus:border-pandora-cyan`}
                disabled={isSaving}
                id="expense-description"
                onChange={(e) =>
                  setEditingExpense({ ...editingExpense, description: e.target.value })
                }
                placeholder="Описание расхода..."
                value={editingExpense.description}
              />
              {errors.description && (
                <p className="mt-1 text-red-500 text-xs">{errors.description}</p>
              )}
            </div>

            {/* Amount & Currency */}
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label
                  className="mb-1.5 flex items-center gap-1 text-[10px] text-gray-500 uppercase"
                  htmlFor="expense-amount"
                >
                  <DollarSign size={12} />
                  Сумма
                </label>
                <input
                  className={`w-full border bg-black ${
                    errors.amount ? "border-red-500" : "border-white/20"
                  } p-3 text-sm text-white outline-none focus:border-pandora-cyan`}
                  disabled={isSaving}
                  id="expense-amount"
                  min="0"
                  onChange={(e) =>
                    setEditingExpense({
                      ...editingExpense,
                      amount: Number.parseFloat(e.target.value) || 0,
                    })
                  }
                  placeholder="0.00"
                  step="0.01"
                  type="number"
                  value={editingExpense.amount || ""}
                />
                {errors.amount && <p className="mt-1 text-red-500 text-xs">{errors.amount}</p>}
              </div>
              <div>
                <label
                  className="mb-1.5 block text-[10px] text-gray-500 uppercase"
                  htmlFor="currency-select"
                >
                  Валюта
                </label>
                <select
                  className="w-full border border-white/20 bg-black p-3 text-sm text-white outline-none focus:border-pandora-cyan"
                  disabled={isSaving}
                  id="currency-select"
                  onChange={(e) =>
                    setEditingExpense({
                      ...editingExpense,
                      currency: e.target.value as "USD" | "RUB",
                    })
                  }
                  value={editingExpense.currency}
                >
                  <option value="USD">USD</option>
                  <option value="RUB">RUB</option>
                </select>
              </div>
            </div>

            {/* Category */}
            <fieldset className="m-0 border-0 p-0">
              <legend className="mb-1.5 flex items-center gap-1 text-[10px] text-gray-500 uppercase">
                <Tag size={12} />
                Категория
              </legend>
              <div className="grid grid-cols-2 gap-2">
                {EXPENSE_CATEGORIES.map((cat) => (
                  <button
                    className={`border p-3 text-left transition-colors ${
                      editingExpense.category === cat.value
                        ? "border-pandora-cyan bg-pandora-cyan/10 text-pandora-cyan"
                        : "border-white/20 bg-black text-gray-300 hover:border-white/40"
                    }`}
                    disabled={isSaving}
                    key={cat.value}
                    onClick={() => handleCategoryChange(cat.value)}
                    type="button"
                  >
                    <div className="flex items-center gap-2 font-bold text-xs">
                      <span>{cat.icon}</span>
                      {cat.label}
                    </div>
                    {cat.value === "marketing" && (
                      <div className="mt-1 text-[9px] text-yellow-400">Списывается из резервов</div>
                    )}
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Date */}
            <div>
              <label
                className="mb-1.5 flex items-center gap-1 text-[10px] text-gray-500 uppercase"
                htmlFor="expense-date"
              >
                <Calendar size={12} />
                Дата
              </label>
              <input
                className={`w-full border bg-black ${
                  errors.date ? "border-red-500" : "border-white/20"
                } p-3 text-sm text-white outline-none focus:border-pandora-cyan`}
                disabled={isSaving}
                id="expense-date"
                onChange={(e) => setEditingExpense({ ...editingExpense, date: e.target.value })}
                type="date"
                value={editingExpense.date || ""}
              />
              {errors.date && <p className="mt-1 text-red-500 text-xs">{errors.date}</p>}
            </div>

            {/* Preview */}
            {editingExpense.amount > 0 && (
              <div className="border border-white/10 bg-white/5 p-3">
                <div className="mb-1 text-[10px] text-gray-500 uppercase">Предпросмотр</div>
                <div className="font-mono text-sm text-white">
                  {formatMoney(editingExpense.amount, editingExpense.currency)}
                </div>
                {selectedCategoryInfo && (
                  <div className="mt-1 text-gray-400 text-xs">
                    {selectedCategoryInfo.icon} {selectedCategoryInfo.label}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                className="flex-1 border border-white/20 bg-white/5 py-3 font-bold text-sm text-white transition-colors hover:bg-white/10 disabled:opacity-50"
                disabled={isSaving}
                onClick={onClose}
                type="button"
              >
                Отмена
              </button>
              <button
                className="flex flex-1 items-center justify-center gap-2 bg-pandora-cyan py-3 font-bold text-black text-sm transition-colors hover:bg-pandora-cyan/80 disabled:opacity-50"
                disabled={isSaving}
                onClick={handleSave}
                type="button"
              >
                <Save size={16} />
                {isSaving ? "Сохранение..." : "Сохранить"}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default memo(ExpenseModal);
